import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { getPolicyPremiumTotal, getPolicyGrandTotal } from "../utils/policyCalc";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Separator } from "../components/ui/separator";
import {
  ArrowLeft,
  User,
  Building2,
  Mail,
  Phone,
  MapPin,
  FileText,
  Car,
  Home,
  Heart,
  Shield,
  AlertCircle,
  Download,
  Pencil,
  Plus,
  Copy,
  AtSign,
  Calendar,
  Euro,
  ExternalLink,
  MessageCircle,
  Clock,
  Link2,
  Power,
  RefreshCw,
  Send
} from "lucide-react";
import { toast } from "sonner";
import { parseISO, isValid, differenceInDays } from "date-fns";
import DocumentsDialog from "../components/DocumentsDialog";
import ClientiCollegati from "../components/ClientiCollegati";
import { DocumentPreviewCard } from "../components/DocumentPreviewCard";
import { Breadcrumb } from "../components/Breadcrumb";
import AuditHistory from "../components/AuditHistory";
import { useConferma } from "../components/ConfirmProvider";
// 29/09/2026 — date gg/mm/aaaa da un'unica fonte (qui c'era formatDateSafe).
import { formatDate, formatDateTime } from "../lib/format";

const formatCurrency = (value) => {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR'
  }).format(value || 0);
};

const getPolicyTypeIcon = (type) => {
  switch (String(type || "").toLowerCase()) {
    case 'auto': case 'moto': case 'autocarri': case 'altri_veicoli':
    case 'cvt': // Corpi Veicoli Terrestri (kasko del mezzo)
      return <Car className="h-4 w-4" />;
    case 'casa':
      return <Home className="h-4 w-4" />;
    case 'vita': case 'salute': case 'infortuni':
      return <Heart className="h-4 w-4" />;
    default:
      return <Shield className="h-4 w-4" />;
  }
};

const getStatusInfo = (policy) => {
  if (policy.status === "cancelled") return { label: "Chiusa", className: "bg-gray-200 text-gray-800 border-gray-400" };
  if (policy.suspended) return { label: "Sospesa", className: "bg-orange-100 text-orange-700 border-orange-300" };
  if (policy.renewed) return { label: "Rinnovata", className: "bg-blue-100 text-blue-700 border-blue-300" };
  if (policy.status === 'expired') return { label: "Scaduta", className: "bg-red-100 text-red-700 border-red-300" };
  return { label: "Attiva", className: "bg-green-100 text-green-700 border-green-300" };
};

const getDaysToExpiry = (expiryDate) => {
  if (!expiryDate) return null;
  try {
    const expiry = parseISO(expiryDate);
    if (!isValid(expiry)) return null;
    return differenceInDays(expiry, new Date());
  } catch {
    return null;
  }
};

export default function ClientDetailPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [policyStorico, setPolicyStorico] = useState([]);  // polizze sostituite da un rinnovo
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  // Distingue "cliente inesistente" da "caricamento fallito": senza questo un
  // problema di rete sembrava che il cliente fosse sparito dall'archivio.
  const [loadError, setLoadError] = useState(false);
  const conferma = useConferma();
  const [documentsDialogOpen, setDocumentsDialogOpen] = useState(false);
  const [portal, setPortal] = useState(null);
  const [portalToken, setPortalToken] = useState(null);
  const [portalBusy, setPortalBusy] = useState(false);

  useEffect(() => {
    fetchClientData();
    fetchPortalStatus();
  }, [clientId]);

  const fetchPortalStatus = async () => {
    try {
      const res = await axios.get(`/api/portal/admin/${clientId}`);
      setPortal(res.data);
    } catch {
      setPortal(null);
    }
  };

  const portalLink = portalToken ? `${window.location.origin}/portale/${portalToken}` : null;

  const handleActivatePortal = async () => {
    setPortalBusy(true);
    try {
      const res = await axios.post('/api/portal/admin/activate', { client_id: clientId });
      setPortal(res.data);
      if (res.data.token) setPortalToken(res.data.token);
      toast.success(res.data.already_active ? "Portale già attivo" : "Portale attivato");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nell'attivazione del portale");
    } finally {
      setPortalBusy(false);
    }
  };

  const handleRegeneratePortal = async () => {
    // v3.9.12 — rigenerare invalida subito il link già inviato al cliente
    if (!(await conferma({
      titolo: "Rigenerare il link del portale?",
      descrizione:
        "Il link che il cliente ha già ricevuto smetterà di funzionare immediatamente. " +
        "Dovrai copiare il nuovo link e inviarglielo di nuovo.",
    }))) return;
    setPortalBusy(true);
    try {
      const res = await axios.post(`/api/portal/admin/${clientId}/regenerate`);
      if (res.data.token) setPortalToken(res.data.token);
      setPortal(prev => ({ ...(prev || {}), active: true, created_at: res.data.created_at }));
      toast.success("Link rigenerato");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nella rigenerazione del link");
    } finally {
      setPortalBusy(false);
    }
  };

  const handleDisablePortal = async () => {
    // v3.9.12 — disattivare rende il link inutilizzabile per il cliente
    if (!(await conferma({
      titolo: "Disattivare il portale del cliente?",
      descrizione: "Il link non funzionerà più finché non lo riattivi.",
    }))) return;
    setPortalBusy(true);
    try {
      await axios.delete(`/api/portal/admin/${clientId}`);
      setPortalToken(null);
      setPortal(prev => ({ ...(prev || {}), active: false }));
      toast.success("Portale disattivato");
    } catch (e) {
      toast.error("Errore nella disattivazione del portale");
    } finally {
      setPortalBusy(false);
    }
  };

  const handleCopyPortalLink = () => {
    if (!portalLink) return;
    navigator.clipboard.writeText(portalLink);
    toast.success("Link copiato");
  };

  const handleSendPortalEmail = async () => {
    if (!portalLink) return;
    setPortalBusy(true);
    try {
      await axios.post('/api/portal/admin/send-email', { client_id: clientId, link: portalLink });
      toast.success("Email inviata al cliente");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nell'invio dell'email");
    } finally {
      setPortalBusy(false);
    }
  };

  const fetchClientData = async () => {
    try {
      setLoading(true);
      const [clientRes, policiesRes, storicoRes, documentsRes] = await Promise.all([
        axios.get(`/api/clients/${clientId}`),
        axios.get('/api/policies?all=true'),
        axios.get('/api/policies?all=true&storico=true'),
        axios.get(`/api/documents?entity_type=client&entity_id=${clientId}`)
      ]);
      
      setClient(clientRes.data);
      const clientPolicies = (policiesRes.data.items || policiesRes.data || []).filter(p => p.client_id === clientId);
      // In scheda si vedono solo le polizze ATTUALI: quelle sostituite da un rinnovo
      // vanno nello storico (richiesta di Gianni del 17/09/2026).
      // Le polizze sostituite arrivano dalla chiamata con storico=true (il backend le
      // toglie dall'elenco normale): in scheda si vedono solo quelle attuali.
      const storicoCliente = (storicoRes.data.items || storicoRes.data || []).filter(p => p.client_id === clientId);
      setPolicies(clientPolicies);
      setPolicyStorico(storicoCliente);
      setDocuments(documentsRes.data || []);
    } catch (error) {
      setLoadError(true);
      toast.error("Errore nel caricamento dei dati cliente");
    } finally {
      setLoading(false);
    }
  };

  const stats = {
    totalPolicies: policies.length,
    activePolicies: policies.filter(p => !p.suspended && !p.renewed && p.status !== 'expired' && p.status !== 'cancelled').length,
    suspendedPolicies: policies.filter(p => p.suspended).length,
    // le polizze chiuse in anticipo non pesano sui totali del cliente
    totalPremium: policies.filter(p => p.status !== 'cancelled').reduce((sum, p) => sum + getPolicyPremiumTotal(p), 0),
    totalConsultation: policies.filter(p => p.status !== 'cancelled').reduce((sum, p) => sum + (p.consultation_fee || 0), 0),
  };

  const handleExportPDF = async () => {
    try {
      const response = await axios.get(`/api/clients/${clientId}/export/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `cliente_${client?.name?.replace(/\s+/g, '_') || clientId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF esportato con successo");
    } catch (error) {
      toast.error("Errore nell'esportazione PDF");
    }
  };

  const handleWhatsApp = () => {
    if (client?.phone) {
      const cleanPhone = client.phone.replace(/\D/g, '');
      const phone = cleanPhone.startsWith('39') ? cleanPhone : `39${cleanPhone}`;
      window.open(`https://wa.me/${phone}`, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="client-detail-loading">
        <Skeleton className="h-4 w-48" />
        <div className="flex gap-4 items-center">
          <Skeleton className="h-16 w-16 rounded-full" />
          <div><Skeleton className="h-8 w-48 mb-2" /><Skeleton className="h-4 w-32" /></div>
        </div>
        <div className="grid gap-4 md:grid-cols-4">{[1,2,3,4].map(i => <Skeleton key={i} className="h-24" />)}</div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (!client) {
    if (loadError) {
      return (
        <div className="text-center py-12 space-y-3">
          <AlertCircle className="h-12 w-12 mx-auto text-amber-500" />
          <h2 className="text-xl font-semibold">Non riesco a caricare il cliente</h2>
          <p className="text-sm text-muted-foreground">
            I dati non sono andati persi: e' il collegamento che non ha risposto.
          </p>
          <div className="flex items-center justify-center gap-2">
            <Button onClick={fetchClientData}>
              <RefreshCw className="h-4 w-4 mr-2" /> Riprova
            </Button>
            <Button variant="outline" onClick={() => navigate('/clients')}>
              <ArrowLeft className="h-4 w-4 mr-2" /> Torna ai Clienti
            </Button>
          </div>
        </div>
      );
    }
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <p className="text-muted-foreground">Cliente non trovato</p>
        <Button onClick={() => navigate('/clients')} className="mt-4">
          <ArrowLeft className="h-4 w-4 mr-2" /> Torna ai Clienti
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="client-detail-page">
      {/* Breadcrumb */}
      <Breadcrumb items={[
        { label: "Clienti", href: "/clients" },
        { label: client.name }
      ]} />

      {/* Hero Header */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border border-border p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-primary/15 border-2 border-primary/30 flex items-center justify-center flex-shrink-0">
              {client.client_type === 'business' ? (
                <Building2 className="h-7 w-7 text-primary" />
              ) : (
                <User className="h-7 w-7 text-primary" />
              )}
            </div>
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground" data-testid="client-name">
                  {client.name}
                </h1>
                <Badge variant={client.client_type === 'business' ? 'default' : 'secondary'}>
                  {client.client_type === 'business' ? 'Azienda' : 'Privato'}
                </Badge>
              </div>
              <p className="text-muted-foreground text-sm mt-1">
                {client.fiscal_code && <span className="font-mono">{client.fiscal_code}</span>}
                {client.fiscal_code && client.city && <span className="mx-2">·</span>}
                {client.city && <span>{client.city}{client.province ? ` (${client.province})` : ''}</span>}
              </p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {client.phone && (
              <Button variant="outline" size="sm" onClick={() => window.location.href = `tel:${client.phone}`} data-testid="call-btn">
                <Phone className="h-4 w-4 mr-1.5" /> Chiama
              </Button>
            )}
            {client.phone && (
              <Button variant="outline" size="sm" onClick={handleWhatsApp} className="text-green-600 border-green-300 hover:bg-green-50" data-testid="whatsapp-btn">
                <MessageCircle className="h-4 w-4 mr-1.5" /> WhatsApp
              </Button>
            )}
            {client.email && (
              <Button variant="outline" size="sm" onClick={() => window.location.href = `mailto:${client.email}`} data-testid="email-btn">
                <Mail className="h-4 w-4 mr-1.5" /> Email
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => setDocumentsDialogOpen(true)} data-testid="docs-btn">
              <FileText className="h-4 w-4 mr-1.5" /> Documenti ({documents.length})
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportPDF} data-testid="export-pdf-btn">
              <Download className="h-4 w-4 mr-1.5" /> PDF
            </Button>
            <Button size="sm" onClick={() => navigate(`/clients?edit=${clientId}`)} data-testid="edit-client-btn">
              <Pencil className="h-4 w-4 mr-1.5" /> Modifica
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Polizze Totali</p>
            <p className="text-2xl font-bold mt-1">{stats.totalPolicies}</p>
          </CardContent>
        </Card>
        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Attive</p>
            <p className="text-2xl font-bold mt-1 text-green-600">{stats.activePolicies}</p>
          </CardContent>
        </Card>
        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Sospese</p>
            <p className="text-2xl font-bold mt-1 text-orange-600">{stats.suspendedPolicies}</p>
          </CardContent>
        </Card>
        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Premi Totali</p>
            <p className="text-xl font-bold mt-1">{formatCurrency(stats.totalPremium)}</p>
          </CardContent>
        </Card>
        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Consulenze</p>
            <p className="text-xl font-bold mt-1 text-green-600">{formatCurrency(stats.totalConsultation)}</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Column - Policies */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold font-heading">Polizze del Cliente</h2>
            <Button size="sm" onClick={() => navigate(`/policies?client=${clientId}`)} data-testid="new-policy-btn">
              <Plus className="h-4 w-4 mr-1.5" /> Nuova Polizza
            </Button>
          </div>

          {policies.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="text-center py-12">
                <Shield className="h-12 w-12 mx-auto text-muted-foreground/40 mb-4" />
                <p className="text-muted-foreground">Nessuna polizza per questo cliente</p>
                <Button size="sm" variant="outline" className="mt-3" onClick={() => navigate(`/policies?client=${clientId}`)}>
                  <Plus className="h-4 w-4 mr-1.5" /> Crea Prima Polizza
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {policies.map((policy) => {
                const statusInfo = getStatusInfo(policy);
                const daysToExpiry = getDaysToExpiry(policy.expiry_date);
                const isExpiringSoon = daysToExpiry !== null && daysToExpiry <= 30 && daysToExpiry >= 0;
                const isExpired = daysToExpiry !== null && daysToExpiry < 0;
                
                return (
                  <Card 
                    key={policy.policy_id}
                    className={`cursor-pointer hover:shadow-md transition-all hover:border-primary/30 group ${
                      isExpired && !policy.renewed ? 'border-red-300 bg-red-50/30 dark:bg-red-950/10' : 
                      isExpiringSoon && !policy.renewed ? 'border-orange-300 bg-orange-50/30 dark:bg-orange-950/10' : ''
                    }`}
                    onClick={() => navigate(`/policies/${policy.policy_id}`)}
                    data-testid={`policy-card-${policy.policy_id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0 group-hover:bg-primary/10 transition-colors">
                            {getPolicyTypeIcon(policy.policy_type)}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-semibold text-foreground group-hover:text-primary transition-colors">
                                {policy.policy_number}
                              </p>
                              <Badge variant="outline" className={statusInfo.className + " text-xs"}>
                                {statusInfo.label}
                              </Badge>
                              {policy.payment_frequency === "semiannual" && (
                                <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-300">
                                  Semestrale
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground mt-0.5">
                              {policy.company} · <span className="capitalize">{policy.policy_type}</span>
                              {policy.license_plate && <span> · <span className="font-mono">{policy.license_plate}</span></span>}
                            </p>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="font-bold text-foreground">{formatCurrency(getPolicyGrandTotal(policy))}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Scade: {formatDate(policy.expiry_date, "-")}
                          </p>
                          {isExpiringSoon && !policy.renewed && (
                            <p className="text-xs font-medium text-orange-600 mt-0.5">
                              tra {daysToExpiry} giorni
                            </p>
                          )}
                          {isExpired && !policy.renewed && (
                            <p className="text-xs font-medium text-red-600 mt-0.5">Scaduta</p>
                          )}
                        </div>
                      </div>
                      {policy.payment_frequency === "semiannual" && policy.semiannual_expiry && (
                        <div className="mt-2 pt-2 border-t border-border/50 flex items-center gap-2 text-xs">
                          <Clock className="h-3 w-3 text-muted-foreground" />
                          <span className="text-muted-foreground">Rata semestrale:</span>
                          <span className={policy.semiannual_paid ? 'text-green-600 font-medium' : 'text-orange-600 font-medium'}>
                            {formatDate(policy.semiannual_expiry, "-")}
                            {policy.semiannual_paid ? " (Pagata)" : " (Da pagare)"}
                          </span>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
          {/* Storico polizze del cliente: le sostituite da un rinnovo, con i loro documenti */}
          {policyStorico.length > 0 && (
            <details className="mt-4 rounded-lg border bg-muted/30" data-testid="storico-polizze-cliente">
              <summary className="cursor-pointer p-3 text-sm font-medium">
                🕘 Storico polizze ({policyStorico.length})
              </summary>
              <div className="p-3 pt-0 space-y-2">
                <p className="text-xs text-muted-foreground">
                  Polizze sostituite da un rinnovo: qui restano i loro dati e i loro documenti.
                </p>
                {policyStorico.map((p) => (
                  <div
                    key={p.policy_id}
                    className="flex items-center justify-between gap-2 rounded border bg-background p-2 cursor-pointer hover:border-primary/40"
                    onClick={() => navigate(`/policies/${p.policy_id}`)}
                    data-testid={`storico-policy-${p.policy_id}`}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{p.policy_number} · {p.company}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(p.expiry_date, "-")}{p.license_plate ? ` · ${p.license_plate}` : ""}
                      </p>
                    </div>
                    <span className="text-xs text-blue-600 whitespace-nowrap">storica →</span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>

        {/* Sidebar - Info */}
        <div className="space-y-4">
          {/* Contact Info */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Contatti</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {client.email && (
                <a href={`mailto:${client.email}`} className="flex items-center gap-2.5 text-sm hover:text-primary transition-colors group" data-testid="client-email">
                  <Mail className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
                  <span className="truncate">{client.email}</span>
                  <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity ml-auto flex-shrink-0" />
                </a>
              )}
              {client.email_alias && (
                <div className="flex items-center gap-2.5 text-sm">
                  <AtSign className="h-4 w-4 text-orange-500" />
                  <span className="font-mono text-xs truncate flex-1">{client.email_alias}</span>
                  <button
                    onClick={() => { navigator.clipboard.writeText(client.email_alias); toast.success("Alias copiato"); }}
                    className="text-muted-foreground hover:text-primary flex-shrink-0"
                    data-testid="copy-alias-btn"
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
              {client.phone && (
                <a href={`tel:${client.phone}`} className="flex items-center gap-2.5 text-sm hover:text-primary transition-colors group" data-testid="client-phone">
                  <Phone className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
                  <span>{client.phone}</span>
                </a>
              )}
              {!client.email && !client.phone && (
                <p className="text-sm text-muted-foreground italic">Nessun contatto</p>
              )}
            </CardContent>
          </Card>

          {/* Address */}
          {(client.address || client.city) && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Indirizzo</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-start gap-2.5 text-sm">
                  <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <div>
                    {client.address && <p>{client.address}</p>}
                    <p>{[client.cap, client.city, client.province].filter(Boolean).join(' - ')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Personal / Business Info */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                {client.client_type === 'business' ? 'Info Azienda' : 'Info Personali'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              {client.client_type === 'business' ? (
                <>
                  {client.referent && <p><span className="text-muted-foreground">Referente:</span> <span className="font-medium">{client.referent}</span></p>}
                  {client.pec && <p><span className="text-muted-foreground">PEC:</span> <span className="font-medium">{client.pec}</span></p>}
                  {client.sdi_code && <p><span className="text-muted-foreground">Codice SDI:</span> <span className="font-mono font-medium">{client.sdi_code}</span></p>}
                  {!client.referent && !client.pec && !client.sdi_code && <p className="text-muted-foreground italic">Nessuna info</p>}
                </>
              ) : (
                <>
                  {client.birth_date && <p><span className="text-muted-foreground">Nato/a il:</span> <span className="font-medium">{formatDate(client.birth_date, "-")}</span></p>}
                  {client.birth_place && <p><span className="text-muted-foreground">Luogo:</span> <span className="font-medium">{client.birth_place}{client.birth_place_province ? ` (${client.birth_place_province})` : ''}</span></p>}
                  {client.gender && <p><span className="text-muted-foreground">Sesso:</span> <span className="font-medium">{client.gender === 'M' ? 'Maschio' : 'Femmina'}</span></p>}
                  {!client.birth_date && !client.birth_place && !client.gender && <p className="text-muted-foreground italic">Nessuna info</p>}
                </>
              )}
            </CardContent>
          </Card>

          {/* 12/10/2026 — familiari e collegati */}
          <ClientiCollegati clientId={clientId} />

          {/* Portale cliente */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Portale cliente</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {portal?.active ? (
                <>
                  {portalToken ? (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-mono text-xs truncate flex-1 break-all">{portalLink}</span>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Portale attivo. Il link è personale: per rivederlo usa "Rigenera".
                    </p>
                  )}
                  {portal.last_access_at && (
                    <p className="text-xs text-muted-foreground">
                      Ultimo accesso: {formatDateTime(portal.last_access_at, "-")}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2">
                    {portalToken && (
                      <>
                        <Button size="sm" variant="outline" onClick={handleCopyPortalLink} disabled={portalBusy}>
                          <Copy className="h-3.5 w-3.5 mr-1.5" /> Copia
                        </Button>
                        <Button size="sm" variant="outline" onClick={handleSendPortalEmail} disabled={portalBusy || !client.email}>
                          <Send className="h-3.5 w-3.5 mr-1.5" /> Invia email
                        </Button>
                      </>
                    )}
                    <Button size="sm" variant="outline" onClick={handleRegeneratePortal} disabled={portalBusy}>
                      <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Rigenera
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-600 border-red-300" onClick={handleDisablePortal} disabled={portalBusy}>
                      <Power className="h-3.5 w-3.5 mr-1.5" /> Disattiva
                    </Button>
                  </div>
                  <div className="text-xs text-muted-foreground pt-1 border-t border-border/50">
                    Avviso scadenze: {portal.notif_enabled ? `attivo → ${portal.notif_email || 'mail non impostata'}` : "non attivato dal cliente"}
                  </div>
                </>
              ) : (
                <Button size="sm" onClick={handleActivatePortal} disabled={portalBusy}>
                  <Link2 className="h-3.5 w-3.5 mr-1.5" /> Attiva portale
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Documents Preview */}
          <DocumentPreviewCard
            entityType="client"
            entityId={clientId}
            entityName={client.name}
            onOpenAll={() => setDocumentsDialogOpen(true)}
            maxItems={3}
          />

          {/* Notes */}
          {client.notes && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Note</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm whitespace-pre-wrap">{client.notes}</p>
              </CardContent>
            </Card>
          )}

          {/* v3.9.12 — storico modifiche del cliente */}
          <AuditHistory entityType="client" entityId={clientId} />
        </div>
      </div>

      <DocumentsDialog
        open={documentsDialogOpen}
        onOpenChange={setDocumentsDialogOpen}
        entityType="client"
        entityId={clientId}
        entityName={client.name}
        onDocumentsChange={fetchClientData}
      />
    </div>
  );
}
